<template>
  <iColumn
    :theme-id="0"
    align="center"
    :width="getOptionValue(LayoutWidth)"
    :height="getOptionValue(LayoutHeight)"
    :max-width="getOptionValue(MaxWidth)"
    :max-height="getOptionValue(MaxHeight)"
    :min-width="getOptionValue(MinWidth)"
    :min-height="getOptionValue(MinHeight)"
    :style-overrides="{
      backgroundColor: 'paper',
      paddingTop: 'small',
      paddingBottom: 'small',
      paddingLeft: 'small',
      paddingRight: 'small',
    }"
  >
    <iSpace :height="100" width="fill" />
    <iColumn :max-width="600" align="center">
      <iRow align="center" :max-width="300">
        <iImageContainer :src="publicationLogoSrc" :style-overrides="{backgroundColor: 'none'}" />
      </iRow>
      <iColumn gap="none" align="center">
        <iDisplayText v-if="publicationName" variant="small" align="center">
          {{ "Join " + publicationName }}
        </iDisplayText>
        <iText v-if="publicationData.tagline" text-align="center">
          {{ publicationData.tagline }}
        </iText>
      </iColumn>
    </iColumn>

    <iRow align="center">
      <iColumn :max-width="400" align="center">
        <iColumn gap="none" align="center" vertical-align="middle">
          <iColumn v-if="editMode" align="center">
            <iText text-align="center">
              Start by entering your email address
            </iText>
            <iRow wrap="nowrap" :max-width="400" align="center">
              <iTextInput :model-value="''" placeholder="your@email.com" width="fill" />
              <iButton>Continue</iButton>
            </iRow>
            <iSpace :height="40" width="fill" />
            <PoweredByInspiration />
          </iColumn>
          <template v-else>
            <JoinPublicationEmail v-if="currentUserFlowStep === EMAIL_FORM">
              <template #below-form>
                <iColumn
                  v-if="guestJoinUrl"
                  :max-width="400"
                  width="fill"
                  align="center"
                  gap="small"
                  data-test="login-guest-option"
                  :style-overrides="{paddingTop: 'standard'}"
                >
                  <iRow
                    vertical-align="middle"
                    gap="small"
                    wrap="nowrap"
                    width="fill"
                    :max-width="160"
                  >
                    <iColumn width="fill">
                      <iDivider color="light" />
                    </iColumn>
                    <iText
                      variant="subtle"
                      font-size="small"
                      width="hug"
                      :wrap="false"
                      :overflow="true"
                    >
                      or
                    </iText>
                    <iColumn width="fill">
                      <iDivider color="light" />
                    </iColumn>
                  </iRow>
                  <iButton
                    variant="text"
                    data-test="login-join-as-guest"
                    @click="joinAsGuest"
                  >
                    Join as a guest
                  </iButton>
                </iColumn>
              </template>
            </JoinPublicationEmail>
            <JoinPublicationPassword v-if="currentUserFlowStep === PASSWORD_FORM" />
            <JoinPublicationSignUp v-if="currentUserFlowStep === SIGN_UP_FORM" />
            <JoinPublicationCompleteAccount v-if="currentUserFlowStep === COMPLETE_ACCOUNT_FORM" />
            <JoinPublicationExistingUser v-if="currentUserFlowStep === SELECT_USER_FORM" />
            <JoinPublicationPrivatePublication v-if="currentUserFlowStep === PRIVATE_NOTICE" />
            <JoinPublicationForgotPassword v-if="currentUserFlowStep === FORGOT_PASSWORD_FORM" />
            <JoinPublicationTwoFactor v-if="currentUserFlowStep === TWO_FACTOR_FORM" />
            <JoinPublicationTwoFactorEnroll v-if="currentUserFlowStep === TWO_FACTOR_ENROLL_FORM" />
          </template>
        </iColumn>
      </iColumn>
    </iRow>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { useFanPublicationStore } from "%/dynamic-component-editor/stores/fan-publication-store";
import { useLoginSignupStore } from "%/dynamic-component-editor/stores/login-signup-store";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { CONTRAST_DARK_MODE } from "%/dynamic-component-editor/constants/color-palette-editor-constants";
import { resolveInitialFlowStep } from "%/dynamic-component-editor/helpers/login-flow-steps";
import {
  COMPLETE_ACCOUNT_FORM,
  EMAIL_FORM,
  FORGOT_PASSWORD_FORM,
  PASSWORD_FORM,
  PRIVATE_NOTICE,
  SELECT_USER_FORM,
  SIGN_UP_FORM,
  TWO_FACTOR_ENROLL_FORM,
  TWO_FACTOR_FORM,
} from "%/dynamic-component-editor/constants/login-flow-constants";
import JoinPublicationEmail from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationEmail.vue";
import JoinPublicationPassword from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationPassword.vue";
import JoinPublicationSignUp from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationSignUp.vue";
import JoinPublicationCompleteAccount from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationCompleteAccount.vue";
import JoinPublicationExistingUser from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationExistingUser.vue";
import JoinPublicationPrivatePublication from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationPrivatePublication.vue";
import JoinPublicationForgotPassword from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationForgotPassword.vue";
import JoinPublicationTwoFactor from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationTwoFactor.vue";
import JoinPublicationTwoFactorEnroll from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/JoinPublicationTwoFactorEnroll.vue";
import PoweredByInspiration from "%/dynamic-component-editor/standard-pages/login-signup-page/join-publication/PoweredByInspiration.vue";
import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutHeight from "%/dynamic-component-editor/options/layout-height";
import MaxWidth from "%/dynamic-component-editor/options/max-width";
import MaxHeight from "%/dynamic-component-editor/options/max-height";
import MinWidth from "%/dynamic-component-editor/options/min-width";
import MinHeight from "%/dynamic-component-editor/options/min-height";

export default {
  name: "LoginSignup",
  components: {
    JoinPublicationEmail,
    JoinPublicationPassword,
    JoinPublicationSignUp,
    JoinPublicationCompleteAccount,
    JoinPublicationExistingUser,
    JoinPublicationPrivatePublication,
    JoinPublicationForgotPassword,
    JoinPublicationTwoFactor,
    JoinPublicationTwoFactorEnroll,
    PoweredByInspiration,
  },
  mixins: [DynamicElementMixin],
  optionBundles: [
    new LayoutWidthHeightOptions({
      "default.layout-width": "fill",
      "default.layout-height": "hug",
    }),
  ],
  elementPanelSettings: {
    displayName: "Login / Signup",
    displayIcon: "streamline-ultimate:login-key",
    elementGroup: "loginSignup",
    elementType: "element",
  },
  computed: {
    ...mapState(useFanPublicationStore, ["publicationData", "publicationName", "publicationLogo", "publicationLogoDark"]),
    ...mapState(useLoginSignupStore, ["currentUserFlowStep", "guestJoinUrl"]),
    publicationLogoSrc() {
      if (this.resolvedThemeMode === CONTRAST_DARK_MODE) {
        return this.publicationLogoDark;
      }
      return this.publicationLogo;
    },
    EMAIL_FORM: () => EMAIL_FORM,
    PASSWORD_FORM: () => PASSWORD_FORM,
    SIGN_UP_FORM: () => SIGN_UP_FORM,
    COMPLETE_ACCOUNT_FORM: () => COMPLETE_ACCOUNT_FORM,
    SELECT_USER_FORM: () => SELECT_USER_FORM,
    PRIVATE_NOTICE: () => PRIVATE_NOTICE,
    FORGOT_PASSWORD_FORM: () => FORGOT_PASSWORD_FORM,
    TWO_FACTOR_FORM: () => TWO_FACTOR_FORM,
    TWO_FACTOR_ENROLL_FORM: () => TWO_FACTOR_ENROLL_FORM,
    LayoutWidth: () => LayoutWidth,
    LayoutHeight: () => LayoutHeight,
    MaxWidth: () => MaxWidth,
    MaxHeight: () => MaxHeight,
    MinWidth: () => MinWidth,
    MinHeight: () => MinHeight,
  },
  created() {
    if (this.editMode) {
      return;
    }
    const store = useLoginSignupStore();
    store.userFlowSteps = [resolveInitialFlowStep({
      loginWithInspirationRequestId: store.loginWithInspirationRequestId,
      userId: store.userId,
      lastLoggedInUsers: store.lastLoggedInUsers,
      userAuthEmail: store.userAuthEmail,
    })];
  },
  methods: {
    joinAsGuest() {
      window.location.assign(this.guestJoinUrl);
    },
  },
};
</script>
